import {
  StudentDateOfBirthFieldErrors,
  StudentDateOfBirthValue,
  validateStudentDateOfBirthRequired,
} from './StudentDateOfBirthFields';

export type EditStudentFormValues = {
  firstName: string;
  lastName: string;
  email: string;
  hasUserId: boolean;
  dateOfBirth: StudentDateOfBirthValue;
  educationSystemKey: string;
  passFailThreshold: string;
};

export type EditStudentFormErrors = {
  firstName?: string;
  lastName?: string;
  email?: string;
  dateOfBirth?: StudentDateOfBirthFieldErrors;
  educationSystemKey?: string;
  passFailThreshold?: string;
};

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function validateEditStudentForm(values: EditStudentFormValues): EditStudentFormErrors {
  const errors: EditStudentFormErrors = {};

  if (!values.firstName.trim()) {
    errors.firstName = 'First name is required.';
  }

  if (!values.lastName.trim()) {
    errors.lastName = 'Last name is required.';
  }

  const email = values.email.trim();
  if (values.hasUserId && !email) {
    errors.email = 'Email is required for students with a login account.';
  } else if (email && !emailPattern.test(email)) {
    errors.email = 'Email format is invalid.';
  }

  const dateOfBirthErrors = validateStudentDateOfBirthRequired(values.dateOfBirth);
  if (Object.keys(dateOfBirthErrors).length > 0) {
    errors.dateOfBirth = dateOfBirthErrors;
  }

  if (!values.educationSystemKey.trim()) {
    errors.educationSystemKey = 'Education system is required.';
  }

  if (values.passFailThreshold.trim()) {
    const threshold = parseInt(values.passFailThreshold, 10);
    if (Number.isNaN(threshold) || threshold < 0 || threshold > 100) {
      errors.passFailThreshold = 'Please enter a number between 0 and 100.';
    }
  }

  return errors;
}

export function isEditStudentFormValid(errors: EditStudentFormErrors): boolean {
  return Object.keys(errors).length === 0;
}

export function getEditStudentInvalidFieldKeys(errors: EditStudentFormErrors): string[] {
  const keys: string[] = [];
  if (errors.firstName) keys.push('firstName');
  if (errors.lastName) keys.push('lastName');
  if (errors.email) keys.push('email');
  if (errors.dateOfBirth?.month) keys.push('dateOfBirth-month');
  if (errors.dateOfBirth?.year) keys.push('dateOfBirth-year');
  if (errors.educationSystemKey) keys.push('educationSystemKey');
  if (errors.passFailThreshold) keys.push('passFailThreshold');
  return keys;
}

export function getVisibleEditStudentFieldError(
  fieldKey: string,
  showErrorsFor: ReadonlySet<string>,
  message?: string
): string | undefined {
  return showErrorsFor.has(fieldKey) ? message : undefined;
}

export function getVisibleDateOfBirthErrors(
  showErrorsFor: ReadonlySet<string>,
  errors?: StudentDateOfBirthFieldErrors
): StudentDateOfBirthFieldErrors {
  if (!errors) return {};

  return {
    month: showErrorsFor.has('dateOfBirth-month') ? errors.month : undefined,
    year: showErrorsFor.has('dateOfBirth-year') ? errors.year : undefined,
  };
}
